import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ClipboardList, Send, CheckCircle, AlertCircle, Loader2, MapPin, Download, RefreshCw } from 'lucide-react';
import MapEditor from '../components/maps/MapEditor';
import api from '../lib/api';
import { renderRichText, isHttpUrl } from '../lib/richText';

function formatAnswerValue(raw?: string): string {
  if (!raw) return '—';
  if (raw.startsWith('{') || raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return '—';
        return parsed
          .map((item: any, index: number) => {
            const name = item?.name || item?.description || `Punkt ${index + 1}`;
            const hasCoords = item?.lat !== undefined || item?.lon !== undefined;
            return hasCoords ? `${name} (${item.lat ?? ''}, ${item.lon ?? ''})` : name;
          })
          .join('\n');
      }
      if (parsed && typeof parsed === 'object') {
        if (parsed.value !== undefined) return String(parsed.value);
        return Object.entries(parsed).map(([key, value]) => `${key}: ${value}`).join('\n');
      }
    } catch {
      // Rohwert anzeigen
    }
  }
  return raw;
}

interface DataField {
  id: string;
  name: string;
  type: string;
  description?: string;
}

interface QuestionnaireField {
  id: string;
  dataFieldId: string;
  dataField: DataField;
  label?: string;
  required: boolean;
  sortOrder: number;
}

const fieldTypeLabels: Record<string, string> = {
  TEXT: 'Text', NUMBER: 'Zahl', BOOLEAN: 'Ja/Nein', DATE: 'Datum',
  POI: 'POI', POI_LIST: 'POI-Liste', COORDINATE_PAIR: 'Koordinaten', SELECT: 'Auswahl',
};

function PoIListEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [items, setItems] = React.useState<any[]>(() => {
    try { const p = JSON.parse(value || "[]"); return Array.isArray(p) ? p : []; } catch { return []; }
  });
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);

  React.useEffect(() => { onChange(JSON.stringify(items)); }, [items]);

  const updateItem = (idx: number, patch: any) => {
    const c = [...items]; c[idx] = { ...c[idx], ...patch }; setItems(c);
  };

  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} draggable
          onDragStart={() => setDragIdx(idx)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragIdx === null || dragIdx === idx) return;
            const c = [...items]; const [m] = c.splice(dragIdx, 1); c.splice(idx, 0, m); setItems(c);
            setDragIdx(null);
          }}
          onDragEnd={() => setDragIdx(null)}
          className={"border rounded-lg p-3 bg-gray-50 space-y-2 transition-opacity " + (dragIdx === idx ? "opacity-50 border-dashed border-primary" : "border-gray-200")}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="cursor-grab text-gray-400 select-none">☰</span>
              <span className="text-xs font-medium text-gray-500">POI #{idx + 1}</span>
            </div>
            <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))}
              className="btn-ghost text-xs text-red-500 p-0.5">🗑</button>
          </div>
          <input type="text" className="input-field" placeholder="Name *" value={item.name || ""}
            onChange={(e) => updateItem(idx, { name: e.target.value })} />
          <input type="text" className="input-field" placeholder="Beschreibung" value={item.description || ""}
            onChange={(e) => updateItem(idx, { description: e.target.value })} />
          <div className="flex gap-2">
            <input type="number" step="any" className="input-field flex-1" placeholder="Breitengrad" value={item.lat || ""}
              onChange={(e) => updateItem(idx, { lat: parseFloat(e.target.value) || 0 })} />
            <input type="number" step="any" className="input-field flex-1" placeholder="Längengrad" value={item.lon || ""}
              onChange={(e) => updateItem(idx, { lon: parseFloat(e.target.value) || 0 })} />
          </div>
        </div>
      ))}
      <button type="button" onClick={() => setItems([...items, { name: "", description: "", lat: 0, lon: 0 }])}
        className="btn-secondary text-sm flex w-full items-center justify-center gap-1">
        + POI hinzufügen
      </button>
    </div>
  );
}

export default function AnonymousFillPage() {
  const { token } = useParams<{ token: string }>();
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submission, setSubmission] = useState<any>(null);
  const [error, setError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const { data: questionnaire, isLoading, isError } = useQuery<any>({
    queryKey: ['questionnaire-anonymous', token],
    queryFn: async () => {
      const res = await api.get(`/questionnaires/fill/anonymous/${token}`);
      return res.data;
    },
    enabled: !!token,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const fieldValues = Object.entries(values)
        .map(([fieldId, value]) => ({ fieldId, value }));
      return api.post(`/questionnaires/submit-anonymous/${token}`, { values: fieldValues });
    },
    onSuccess: (res) => { setSubmission(res.data); setSubmitted(true); },
    onError: (err: any) => setError(err.response?.data?.message || err.message),
  });

  const afterSubmitAction = questionnaire?.afterSubmitAction ?? 'REFILL';
  const redirectTarget =
    afterSubmitAction === 'REDIRECT' && isHttpUrl(questionnaire?.redirectUrl)
      ? (questionnaire.redirectUrl as string)
      : null;

  useEffect(() => {
    if (submitted && redirectTarget) {
      window.location.replace(redirectTarget);
    }
  }, [submitted, redirectTarget]);

  const submittedEntryId = submission?.entryId;

  const downloadPdf = async () => {
    if (!token || !submittedEntryId) return;
    setPdfLoading(true);
    setPdfError('');
    try {
      const res = await api.get(
        `/questionnaires/fill/anonymous/${token}/entries/${submittedEntryId}/pdf`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `einreichung-${submittedEntryId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setPdfError(err.response?.data?.message || 'PDF konnte nicht erstellt werden');
    } finally {
      setPdfLoading(false);
    }
  };

  const resetForm = () => {
    setValues({});
    setSubmission(null);
    setError('');
    setPdfError('');
    setSubmitted(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="card text-center py-12 max-w-md">
          <AlertCircle size={40} className="mx-auto text-red-400 mb-3" />
          <p className="text-red-500 font-medium">Fragebogen nicht gefunden</p>
          <p className="text-xs text-gray-400 mt-2">Der Link ist ungültig, abgelaufen oder wurde deaktiviert.</p>
        </div>
      </div>
    );
  }

  const sortedFields = [...(questionnaire?.fields || [])].sort((a: any, b: any) => a.sortOrder - b.sortOrder);

  if (submitted) {
    if (redirectTarget) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="card text-center py-12 max-w-md space-y-3">
            <Loader2 size={32} className="mx-auto animate-spin text-primary" />
            <p className="text-sm text-gray-500">Du wirst weitergeleitet …</p>
            <a href={redirectTarget} className="text-sm text-primary underline break-all">{redirectTarget}</a>
          </div>
        </div>
      );
    }

    if (afterSubmitAction === 'THANKS_TEXT') {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="card px-8 py-10 max-w-lg">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={40} className="text-green-500 shrink-0" />
              <h2 className="text-xl font-bold text-gray-900">Vielen Dank!</h2>
            </div>
            <div
              className="text-sm text-gray-700 space-y-2 [&_a]:text-primary [&_a]:underline [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              dangerouslySetInnerHTML={{ __html: renderRichText(questionnaire?.thanksText) }}
            />
          </div>
        </div>
      );
    }

    if (afterSubmitAction === 'SHOW_READONLY') {
      return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="card">
              <div className="border-b border-gray-200 px-6 py-4 flex items-center gap-3">
                <CheckCircle size={28} className="text-green-500 shrink-0" />
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Einreichung erfolgreich!</h2>
                  <p className="text-sm text-gray-500">
                    Deine Antworten für <strong>{questionnaire?.name}</strong> wurden anonym übermittelt.
                  </p>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {sortedFields.length === 0 ? (
                  <p className="text-sm text-gray-400">Dieser Fragebogen hat keine Felder.</p>
                ) : (
                  sortedFields.map((qf: any) => (
                    <div key={qf.id}>
                      <p className="text-xs font-medium text-gray-500">
                        {qf.label || qf.dataField?.label || qf.dataField?.name || 'Feld'}
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">
                        {formatAnswerValue(values[qf.dataFieldId])}
                      </p>
                    </div>
                  ))
                )}

                {pdfError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                    {pdfError}
                  </div>
                )}

                <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-100">
                  {questionnaire?.pdfEnabled && submittedEntryId && (
                    <button onClick={downloadPdf} disabled={pdfLoading} className="btn-secondary">
                      {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                      PDF herunterladen
                    </button>
                  )}
                  <span className="self-center text-xs text-gray-400">
                    Diese Seite kann jetzt geschlossen werden.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="card text-center py-12 max-w-md space-y-4">
          <CheckCircle size={48} className="mx-auto text-green-500" />
          <h2 className="text-xl font-bold text-gray-900">Einreichung erfolgreich! 🎉</h2>
          <p className="text-sm text-gray-500">
            Deine Antworten für <strong>{questionnaire?.name}</strong> wurden anonym übermittelt.
          </p>
          <p className="text-xs text-gray-400">Dieses Fenster kann jetzt geschlossen werden.</p>
          <button onClick={resetForm} className="btn-primary">
            <RefreshCw size={16} /> Erneut ausfüllen
          </button>
        </div>
      </div>
    );
  }

  const renderFieldInput = (qf: any) => {
    const field = qf.dataField || {};
    const val = values[qf.dataFieldId] ?? '';
    const setVal = (v: string) => setValues(prev => ({ ...prev, [qf.dataFieldId]: v }));

    switch (field.type) {
      case 'NUMBER':
        return <input type="number" className="input-field" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Zahl eingeben..." required={qf.required} />;
      case 'BOOLEAN':
        return (
          <select className="input-field" value={val} onChange={(e) => setVal(e.target.value)} required={qf.required}>
            <option value="">Bitte wählen</option>
            <option value="true">Ja</option>
            <option value="false">Nein</option>
          </select>
        );
      case 'DATE':
        return <input type="date" className="input-field" value={val} onChange={(e) => setVal(e.target.value)} required={qf.required} />;
      case 'COORDINATE_PAIR':
        return (
          <div className="flex gap-2">
            <input type="number" step="any" className="input-field flex-1" placeholder="Breitengrad"
              value={val.split(',')[0] || ''}
              onChange={(e) => { const [, lng] = val.split(','); setVal(e.target.value + ',' + (lng || '')); }}
              required={qf.required} />
            <input type="number" step="any" className="input-field flex-1" placeholder="Längengrad"
              value={val.split(',')[1] || ''}
              onChange={(e) => { const [lat] = val.split(','); setVal((lat || '') + ',' + e.target.value); }}
              required={qf.required} />
          </div>
        );
      case 'POI_LIST':
        return <PoIListEditor value={val} onChange={(v) => setVal(v)} />;
      case 'POI':
        let poiName = '', poiDesc = '', poiLat = '', pooLon = '';
        try { const p = JSON.parse(val); poiName = p.name || ''; poiDesc = p.description || ''; poiLat = String(p.lat || ''); pooLon = String(p.lon || ''); } catch {}
        return (
          <div className="space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <input type="text" className="input-field" placeholder="Name *" value={poiName}
              onChange={(e) => setVal(JSON.stringify({ name: e.target.value, description: poiDesc, lat: parseFloat(poiLat) || 0, lon: parseFloat(pooLon) || 0 }))} />
            <input type="text" className="input-field" placeholder="Beschreibung" value={poiDesc}
              onChange={(e) => setVal(JSON.stringify({ name: poiName, description: e.target.value, lat: parseFloat(poiLat) || 0, lon: parseFloat(pooLon) || 0 }))} />
            <div className="flex gap-2">
              <input type="number" step="any" className="input-field flex-1" placeholder="Breitengrad" value={poiLat}
                onChange={(e) => setVal(JSON.stringify({ name: poiName, description: poiDesc, lat: parseFloat(e.target.value) || 0, lon: parseFloat(pooLon) || 0 }))} />
              <input type="number" step="any" className="input-field flex-1" placeholder="Längengrad" value={pooLon}
                onChange={(e) => setVal(JSON.stringify({ name: poiName, description: poiDesc, lat: parseFloat(poiLat) || 0, lon: parseFloat(e.target.value) || 0 }))} />
            </div>
          </div>
        );
      case 'GEOJSON':
        return <MapEditor value={val} onChange={(v) => setVal(v)} />;
      case 'TEXT':
      default:
        return <input type="text" className="input-field" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Text eingeben..." required={qf.required} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="card">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <ClipboardList size={24} className="text-primary" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{questionnaire?.name}</h1>
                <p className="text-xs text-gray-500 mt-1">Anonyme Einreichung</p>
              </div>
            </div>
            {questionnaire?.description && (
              <p className="mt-2 text-sm text-gray-600">{questionnaire.description}</p>
            )}
          </div>

          <div className="p-6 space-y-5">
            {sortedFields.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">
                Dieser Fragebogen hat noch keine Felder.
              </div>
            ) : (
              sortedFields.map((qf: any) => (
                <div key={qf.id}>
                  <label className="label flex items-center gap-1">
                    {qf.label || qf.dataField?.label || qf.dataField?.name || 'Feld'}
                    {qf.required && <span className="text-red-500">*</span>}
                  </label>
                  {qf.dataField?.description && (
                    <p className="text-xs text-gray-400 mb-1">{qf.dataField.description}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="badge-info text-[10px] shrink-0">
                      {fieldTypeLabels[qf.dataField?.type] || qf.dataField?.type || '?'}
                    </span>
                    <div className="flex-1">{renderFieldInput(qf)}</div>
                  </div>
                </div>
              ))
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <div className="pt-4 border-t border-gray-100">
              <button
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isPending || sortedFields.length === 0}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {submitMutation.isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                Absenden
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
